import { useQuery } from "@tanstack/react-query";

import { partnerPortalService } from "../api/partnerPortalService";
import { PARTNER_PORTAL_KEY } from "./portalKeys";
import { partnerCan, type PartnerPerm } from "./partnerAccess";

/** /me 渠道权限的 query key(不挂 PARTNER_PORTAL_KEY 下:写操作一把刷新时不必跟着重拉)。 */
export const PARTNER_ACCESS_KEY = ["partner-access", "me"] as const;

export interface PartnerAccess {
  /** /me 已有结果(成功或失败)。路由守卫 / 「按权限才发」的查询要等它。 */
  ready: boolean;
  role: string | null;
  /** 当前登录账号 id(账号页标「我」、不让改自己)。 */
  userId: number | null;
  /** null = 不知道(老后端 / 拉取失败)→ 一律放行,服务端兜底。 */
  perms: string[] | null;
  can: (perm: PartnerPerm) => boolean;
}

/**
 * 当前渠道账号的角色与权限点(GET /me 的 partnerRole / partnerPerms,OEM 第二批 A4)。
 * enabled=false(非渠道身份)时恒放行、ready=true。
 */
export function usePartnerAccess(enabled = true): PartnerAccess {
  const q = useQuery({
    queryKey: PARTNER_ACCESS_KEY,
    queryFn: partnerPortalService.me,
    enabled,
    staleTime: 60_000,
    retry: false,
    meta: { suppressGlobalErrorToast: true },
  });
  const perms = Array.isArray(q.data?.partnerPerms) ? q.data!.partnerPerms! : null;
  return {
    ready: !enabled || !q.isPending,
    role: q.data?.partnerRole ?? null,
    userId: q.data?.userId ?? null,
    perms,
    can: (perm) => partnerCan(perms, perm),
  };
}

/**
 * 渠道是否被平台暂停(profile.status ≠ active):暂停 = 后台只读,所有写按钮禁用。
 * 与 features 里 usePartnerProfile 同 key 同 fn,共享缓存。
 */
export function usePartnerSuspended(enabled = true): boolean {
  const q = useQuery({
    queryKey: [...PARTNER_PORTAL_KEY, "profile"],
    queryFn: partnerPortalService.profile,
    staleTime: 30_000,
    enabled,
  });
  return q.data?.status != null && q.data.status !== "active";
}

/** 写操作门:有该权限且渠道没被暂停。 */
export function usePartnerWrite(perm: PartnerPerm): boolean {
  const access = usePartnerAccess();
  const suspended = usePartnerSuspended();
  return access.can(perm) && !suspended;
}
